<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import aiReplyPreferenceAPI from 'dashboard/api/aiReplyPreference';
import conversationsAPI from 'dashboard/api/conversations';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const store = useStore();
const loading = ref(false);
const aiReplySetting = ref(null);
const agentPreference = ref(null);
const { t } = useI18n();

const currentChat = computed(() => store.getters.getSelectedChat);

// 监听会话级别设置
watch(
  () => currentChat.value?.additional_attributes?.ai_reply_enabled,
  newVal => {
    aiReplySetting.value = newVal;
  },
  { immediate: true }
);

// 获取客服级别设置
const fetchAgentPreference = async () => {
  if (!props.conversationId) return;

  try {
    const response = await aiReplyPreferenceAPI.get();
    agentPreference.value = response.data.mode === 'auto_reply';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch agent preference:', error);
    agentPreference.value = false;
  }
};

watch(
  () => currentChat.value?.id,
  () => {
    if (currentChat.value?.id) {
      fetchAgentPreference();
    }
  },
  { immediate: true }
);

const canToggle = computed(() => agentPreference.value === true);

const isInherited = computed(
  () => aiReplySetting.value === null || aiReplySetting.value === undefined
);

const inlineStatusText = computed(() => {
  if (!canToggle.value) {
    return t(
      'CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_STATUS.UNAVAILABLE_SUGGESTION_MODE'
    );
  }

  if (!isInherited.value) {
    return aiReplySetting.value
      ? t(
          'CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_STATUS.ENABLED_CONVERSATION'
        )
      : t(
          'CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_STATUS.DISABLED_CONVERSATION'
        );
  }

  return t(
    'CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_STATUS.ENABLED_INHERITED'
  );
});

const inlineHintText = computed(() => {
  return canToggle.value
    ? t('CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_HINT.TOGGLE')
    : t('CONVERSATION.REPLYBOX.AI_AUTO_REPLY_INLINE_HINT.UNAVAILABLE');
});

const buttonIcon = computed(() => {
  if (!canToggle.value) return 'i-ph-robot-duotone';
  const enabled = isInherited.value
    ? agentPreference.value
    : aiReplySetting.value;
  return enabled ? 'i-ph-robot' : 'i-ph-robot-duotone';
});

const buttonVariant = computed(() => {
  if (!canToggle.value) return 'faded';
  const enabled = isInherited.value
    ? agentPreference.value
    : aiReplySetting.value;
  return enabled ? 'solid' : 'faded';
});

const toggleAiReply = async () => {
  if (!props.conversationId || !canToggle.value) return;

  loading.value = true;
  try {
    let newValue;
    if (isInherited.value) {
      newValue = true;
    } else if (aiReplySetting.value === true) {
      newValue = false;
    } else {
      newValue = null;
    }

    const response = await conversationsAPI.updateAiReplySettings(
      props.conversationId,
      { ai_reply_enabled: newValue }
    );

    store.commit('UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES', {
      conversationId: props.conversationId,
      customAttributes: {
        ...currentChat.value.additional_attributes,
        ai_reply_enabled: response.data.ai_reply_enabled,
      },
    });

    useAlert('AI Reply setting updated');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to update AI Reply setting:', error);
    useAlert('Failed to update AI Reply setting');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="inline-flex items-center gap-2 min-w-0">
    <NextButton
      v-tooltip.top-end="
        $t('CONVERSATION.REPLYBOX.AI_AUTO_REPLY_TOGGLE', {
          status: inlineStatusText,
        }) +
        ' · ' +
        inlineHintText
      "
      :icon="buttonIcon"
      :variant="buttonVariant"
      color="slate"
      sm
      :is-loading="loading"
      :disabled="!canToggle"
      :aria-pressed="canToggle && !isInherited && aiReplySetting"
      @click="toggleAiReply"
    />
    <span
      data-test-id="ai-auto-reply-status"
      class="hidden md:block text-xs text-n-slate-11 select-none whitespace-nowrap"
    >
      {{ inlineStatusText }}
    </span>
  </div>
</template>
